import dayjs from 'dayjs';
import {Fragment, useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {
  Button,
  Caption,
  Divider,
  Headline,
  Subheading,
  Title,
} from 'react-native-paper';
import auth from '@react-native-firebase/auth';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {useLinkTo} from '@react-navigation/native';
import {GoogleSignin} from '@react-native-google-signin/google-signin';

import {
  fallbackLanguageLocale,
  useAppSettings,
} from '../components/AppSettings';
import {getProviders} from '../util/helpers';
import Provider from '../components/AuthProvider';
import {displayNameFromPerson, useAuthPersonInfo} from '../hooks/usePersonInfo';

export const ProfileSettingsScreen = () => {
  const linkTo = useLinkTo();
  const appSettings = useAppSettings();
  const authCurrentUser = auth().currentUser;
  const authPerson = useAuthPersonInfo();
  const [signingOut, setSigningOut] = useState(false);
  let personDisplayName = displayNameFromPerson(authPerson?.person);
  let authUserDisplayName = authCurrentUser?.displayName
    ? authCurrentUser.displayName
    : undefined;
  const displayName = authUserDisplayName ?? personDisplayName;

  async function signOut() {
    setSigningOut(true);
    linkTo('/');
    await GoogleSignin.signOut();
    await auth().signOut();
  }

  if (!authCurrentUser) {
    return null;
  }

  // Array of providers the the user is linked with
  const providers = getProviders(authCurrentUser);

  const themedStyles = StyleSheet.create({
    divider: {
      marginBottom: 10,
      backgroundColor: appSettings.currentTheme.colors.primary,
    },
  });

  return (
    <Fragment>
      <View style={styles.content}>
        <Headline>{appSettings.t('ProfileUserSettingsUserHeader')}</Headline>
        <Divider style={themedStyles.divider} />
        <View style={styles.content}>
          <Title>{displayName}</Title>
          {!!authCurrentUser.displayName && (
            <Title>
              {authCurrentUser.email}{' '}
              {authCurrentUser.emailVerified && (
                <Icon name="check-decagram" color="#2196f3" size={26} />
              )}
            </Title>
          )}
          {!!authCurrentUser.phoneNumber && (
            <Subheading>{authCurrentUser.phoneNumber}</Subheading>
          )}
          {(!!authPerson?.isAdmin || !!authPerson?.isGuestRelations) &&
            !!authPerson?.person.roles && (
              <Caption>{authPerson.person.roles.join(', ')}</Caption>
            )}
          {!!authCurrentUser.metadata.lastSignInTime && (
            <Caption>
              {`${appSettings.t('profileLastSignIn')}: ${dayjs(
                authCurrentUser.metadata.lastSignInTime,
              )
                .locale(
                  appSettings.languageLocale?.languageTag ??
                    fallbackLanguageLocale.languageTag,
                )
                .format()}`}
            </Caption>
          )}
        </View>
      </View>
      <View
        style={[
          styles.providers,
          {backgroundColor: appSettings.currentTheme.colors.surface},
        ]}>
        <Provider type="password" active={providers.includes('password')} />
        <Provider type="facebook" active={providers.includes('facebook.com')} />
        <Provider type="google" active={providers.includes('google.com')} />
        <Provider type="phone" active={providers.includes('phone')} />
      </View>
      <Button
        style={styles.appSettingsButtonStyle}
        mode="contained"
        onPress={() => {
          linkTo('/settings/profile/edit');
        }}>
        {appSettings.t('profile_btnEditProfile')}
      </Button>
      <View style={styles.centeredPadded} />
      <Button
        mode="contained"
        loading={signingOut}
        onPress={() => {
          if (!signingOut) {
            setSigningOut(true);
            signOut();
          }
        }}
        style={styles.appSettingsButtonStyle}>
        {appSettings.t('userSignOut')}
      </Button>
    </Fragment>
  );
};

const styles = StyleSheet.create({
  appBarButtonPadding: {
    padding: 10,
  },
  appSettingsButtonStyle: {
    // padding: 10,
    height: 30,
    width: '80%',
    alignSelf: 'center',
    justifyContent: 'center',
  },
  content: {
    paddingHorizontal: 20,
  },
  centeredPadded: {
    alignSelf: 'center',
    alignContent: 'center',
    padding: 15,
  },
  fullyCenteredPageContainer: {
    flex: 1,
    alignContent: 'center',
    alignItems: 'center',
    justifyContent: 'center',
  },
  pageContainer: {
    flex: 1,
    padding: 20,
  },
  providers: {
    elevation: 4,
    flexDirection: 'row',
    justifyContent: 'space-evenly',
    marginVertical: 30,
    padding: 20,
  },
  divider: {
    height: 4,
  },
  profile: {
    marginTop: -50,
    paddingVertical: 10,
  },
  avatar: {
    borderWidth: 5,
    elevation: 4,
  },
});
